import { ROOT_SERVER_URL } from '../config';
import { collectQueryParams, request } from '../utils';
import { setPaginationParams, type PaginationParams } from './pagination';
import type { AuditFields, TimestampFields } from './types';

interface CreateBuildConfigParams {
  ownerId: string;
  ownerType: 'organization' | 'group' | 'system';
  name: string;
  isDefault: boolean;
}

export interface TriggeredBuild extends TimestampFields {
  id: string;
  organizationId: string;
  groupId: string;
  configId: string;
  triggeredConfig: Record<string, string | string[]>;
  status: string;
  extra: Record<string, string> | null;
  createdBy: string;

  organization: { id: string; name: string } | null;
  group: { id: string; name: string } | null;
  buildConfig: { id: string; name: string; deletedAt: string | null } | null;
  creator: { id: string; email: string; name: string } | null;
  buildNotes: Pick<BuildNote, 'id' | 'title' | 'value' | 'updatedAt' | 'noteCreator'>[];
}

export interface BuildNote extends AuditFields, TimestampFields {
  id: string;
  entityId: string;
  title: string | null;
  value: string;
  noteCreator: { id: string; name: string; isCurrentUser: boolean } | null;
}

export function createBuildConfig(data: CreateBuildConfigParams) {
  return request(`${ROOT_SERVER_URL}/api/builds/configs`, {
    data,
    method: 'POST',
  });
}

export function updateBuildConfig(configId: string, changes: CreateBuildConfigParams) {
  return request(`${ROOT_SERVER_URL}/api/builds/configs/${configId}`, {
    data: changes,
    method: 'PUT',
  });
}

export function getBuildConfigs(urlParams: { ownerId: string; ownerType: 'organization' | 'group' | 'system' }) {
  const urlSearchParams = collectQueryParams(urlParams);
  return request(`${ROOT_SERVER_URL}/api/builds/configs/?${urlSearchParams}`);
}

export function removeBuildConfig(paramId: string) {
  return request(`${ROOT_SERVER_URL}/api/builds/configs/${paramId}`, {
    method: 'DELETE',
  });
}

export function changeBuildConfigStatus(configId: string, data: { disabled: boolean }) {
  return request(`${ROOT_SERVER_URL}/api/builds/configs/status/${configId}`, {
    data,
    method: 'PUT',
  });
}

export function addBuildParameter(data: { configId: string; parameterName: string; parameterValue: string }) {
  return request(`${ROOT_SERVER_URL}/api/builds/parameters`, {
    data,
    method: 'POST',
  });
}

export function addBuildParameters(data: {
  configId: string;
  parameters: { id: string; parameterName: string; parameterValue: string }[];
}) {
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/bulk`, {
    data,
    method: 'POST',
  });
}

export function updateBuildParameter(paramId: string, changes: { configId: string; parameterName: string; parameterValue: string }) {
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/${paramId}`, {
    data: changes,
    method: 'PUT',
  });
}

export function getBuildParameters(urlParams: { configId: string }) {
  const urlSearchParams = collectQueryParams(urlParams);
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/?${urlSearchParams}`);
}

export function removeBuildParameter(paramId: string) {
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/${paramId}`, {
    method: 'DELETE',
  });
}

export function removeConfigParameters(configId: string) {
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/bulk/${configId}`, {
    method: 'DELETE',
  });
}

export function getDefaultBuildWithParameters(urlParams: { ownerId: string; ownerType: 'organization' | 'group' | 'system' }) {
  const urlSearchParams = collectQueryParams(urlParams);
  return request(`${ROOT_SERVER_URL}/api/builds/configs/default/?${urlSearchParams}`);
}

export function getMatchedBuildConfigs(urlParams: { type: string; searchText: string }) {
  const urlSearchParams = collectQueryParams(urlParams);
  return request(`${ROOT_SERVER_URL}/api/builds/configs/matched?${urlSearchParams}`);
}

export function cloneBuildConfiguration(data: { fromConfigId: string; toConfigId: string; toEntity: string; entityType: string }) {
  return request(`${ROOT_SERVER_URL}/api/builds/configs/clone`, {
    data,
    method: 'POST',
  });
}

export function getInheritedBuildParameters(urlParams: { configOwnerId: string; configOwnerType: string; isDefaultConfig: boolean }) {
  const urlSearchParams = collectQueryParams(urlParams);
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/inheritance/?${urlSearchParams}`);
}

export function getSuggestions(urlParams: {
  type: 'parameterName' | 'parameterValue';
  value: string;
  detailed?: boolean;
  parameterName?: string;
}) {
  const urlSearchParams = collectQueryParams(urlParams);
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/suggestions/?${urlSearchParams}`);
}

export function getParameterNames() {
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/dictionary`);
}

export function addParameterName(data: { name: string; type: 'single' | 'multiple' | 'boolean'; description?: string }) {
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/dictionary`, {
    data,
    method: 'POST',
  });
}

export function updateParameterNameData(
  paramId: string,
  changes: { name: string; type: 'single' | 'multiple' | 'boolean'; description?: string }
) {
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/dictionary/${paramId}`, {
    data: changes,
    method: 'PUT',
  });
}

export function deleteParameterName(paramId: string) {
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/dictionary/${paramId}`, {
    method: 'DELETE',
  });
}

export function getParameterValues(urlParams: { parameterId: string }) {
  const urlSearchParams = collectQueryParams(urlParams);
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/meta?${urlSearchParams}`);
}

export function addParameterValueData(data: { parameterId: string; value: string; description?: string | null }) {
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/meta`, {
    data,
    method: 'POST',
  });
}

export function updateParameterValue(paramId: string, changes: { parameterId: string; value: string; description?: string | null }) {
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/meta/${paramId}`, {
    data: changes,
    method: 'PUT',
  });
}

export function deleteParameterValue(paramId: string) {
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/meta/${paramId}`, {
    method: 'DELETE',
  });
}

export function getFinalParameters(urlParams: {
  configOwnerId: string;
  configOwnerType: string;
  isDefaultConfig: boolean;
  configId: string;
}) {
  const urlSearchParams = collectQueryParams(urlParams);
  return request(`${ROOT_SERVER_URL}/api/builds/parameters/final/?${urlSearchParams}`);
}

export function runBuilds(data: { builds: Record<string, Record<string, string>> }) {
  return request(`${ROOT_SERVER_URL}/api/builds/configs/run`, {
    data,
    method: 'POST',
  });
}

export function getTriggeredBuilds(
  params: { search: string | undefined },
  pagination: PaginationParams,
  { signal }: { signal?: AbortSignal } = {}
): Promise<{ count: number; items: TriggeredBuild[] }> {
  const urlSearchParams = collectQueryParams(params);

  setPaginationParams(urlSearchParams, pagination);

  return request(`${ROOT_SERVER_URL}/api/builds/configs/triggered?${urlSearchParams}`, { signal });
}

export function getParentBuildConfigs(urlParams: { ownerId: string; ownerType: string; isDefault: boolean }) {
  const urlSearchParams = collectQueryParams(urlParams);
  return request(`${ROOT_SERVER_URL}/api/builds/configs/parents/?${urlSearchParams}`);
}

export function createBuildNote(data: { entityId: string; title: string; value: string }): Promise<BuildNote> {
  return request(`${ROOT_SERVER_URL}/api/builds/configs/notes`, {
    data,
    method: 'POST',
  });
}

export function updateBuildNote(noteId: string, changes: { entityId: string; title: string; value: string }): Promise<BuildNote> {
  return request(`${ROOT_SERVER_URL}/api/builds/configs/notes/${noteId}`, {
    data: changes,
    method: 'PUT',
  });
}

export function deleteBuildNote(noteId: string) {
  return request(`${ROOT_SERVER_URL}/api/builds/configs/notes/${noteId}`, {
    method: 'DELETE',
  });
}
